/**
 * EnvironmentMapLoader - Lädt große Umgebungsmaps (wie NFS-U2 City)
 */

import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { TextureLoader } from 'three';

export interface EnvironmentMapConfig {
  name: string;
  modelPath: string;
  texturesPath?: string;
  lodLevels?: number;
  scale?: number;
}

export class EnvironmentMapLoader {
  private loader = new GLTFLoader();
  private textureLoader = new TextureLoader();
  private loadedMaps = new Map<string, THREE.Group>();
  private loadingPromises = new Map<string, Promise<THREE.Group>>();

  /**
   * Lädt eine große Umgebungsmap (z.B. NFS-U2 City)
   */
  async loadMap(config: EnvironmentMapConfig): Promise<THREE.Group> {
    const mapId = config.name;

    // Cached
    if (this.loadedMaps.has(mapId)) {
      return this.loadedMaps.get(mapId)!.clone();
    }

    // Bereits wird geladen
    if (this.loadingPromises.has(mapId)) {
      return this.loadingPromises.get(mapId)!;
    }

    const loadPromise = this._loadMapInternal(config);
    this.loadingPromises.set(mapId, loadPromise);

    try {
      const mapGroup = await loadPromise;
      this.loadedMaps.set(mapId, mapGroup);
      return mapGroup.clone();
    } finally {
      this.loadingPromises.delete(mapId);
    }
  }

  /**
   * Interne Ladefunktion
   */
  private async _loadMapInternal(config: EnvironmentMapConfig): Promise<THREE.Group> {
    return new Promise((resolve, reject) => {
      this.loader.load(
        config.modelPath,
        gltf => {
          const mapGroup = new THREE.Group();
          const model = gltf.scene;

          // Apply Scale
          if (config.scale) {
            model.scale.multiplyScalar(config.scale);
          }

          // Optimize Materials
          model.traverse(node => {
            if (node instanceof THREE.Mesh) {
              node.castShadow = true;
              node.receiveShadow = true;

              // Optimiere Material
              if (node.material instanceof THREE.Material) {
                (node.material as THREE.Material).side = THREE.FrontSide;
              }

              // LOD System (vereinfache Geometrie wenn zu viele Polygone)
              const geometry = node.geometry;
              if (geometry.attributes.position && geometry.attributes.position.count > 10000) {
                // Vereinfachte Version für hohe Entfernung
                console.log(`Geometry für ${node.name} hat ${geometry.attributes.position.count} Vertices - LOD empfohlen`);
              }
            }
          });

          mapGroup.add(model);
          mapGroup.name = config.name;
          resolve(mapGroup);
        },
        undefined,
        err => {
          console.error(`Fehler beim Laden der Karte ${config.name}:`, err);
          reject(err);
        }
      );
    });
  }

  /**
   * Lädt Texturen für eine Map
   */
  async loadTextures(texturePath: string): Promise<Map<string, THREE.Texture>> {
    const textures = new Map<string, THREE.Texture>();

    // Beispiel-Texturen (diese würden von außen bereitgestellt)
    const textureFiles = [
      'ARC_CONCRETE.png',
      'TRN_GRASS.png',
      'ARC_BUILDING.png',
      'RDP_ROAD.png'
    ];

    for (const file of textureFiles) {
      try {
        const texture = await new Promise<THREE.Texture>((resolve, reject) => {
          this.textureLoader.load(`${texturePath}/${file}`, resolve, undefined, reject);
        });
        textures.set(file, texture);
      } catch (err) {
        console.warn(`Textur ${file} konnte nicht geladen werden:`, err);
      }
    }

    return textures;
  }

  /**
   * Gibt den Speicher frei
   */
  dispose(): void {
    this.loadedMaps.forEach(map => {
      map.traverse(node => {
        if (node instanceof THREE.Mesh) {
          node.geometry?.dispose();
          if (Array.isArray(node.material)) {
            node.material.forEach(m => m.dispose());
          } else {
            node.material?.dispose();
          }
        }
      });
    });

    this.loadedMaps.clear();
    this.loadingPromises.clear();
  }
}

export default EnvironmentMapLoader;
